<template>
  <div class="game"
    :class="{
      'game--ready': game.phase === 'ready',
      'game--running': game.phase === 'running',
      'game--paused': game.phase === 'paused',
      'game--dying': game.phase === 'dying' || game.phase === 'gameover'
    }"
  >
    <canvas id="game" ref="canvasRef"></canvas>

    <ParallaxBackground ref="parallax" />

    <GameGui
      :coin-count="game.coinCount"
      :life-count="game.lifeCount"
      :is-muted="isMuted"
      :is-paused="isPaused"
      :current-button="currentButton"
      @toggle-mute="onToggleMute"
      @toggle-pause="onTogglePause"
      @start="onStartClick"
      @restart="onRestartClick"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, computed } from 'vue';
import { initGame, pauseGame, resumeGame, isGamePaused, startGame, restartGame } from '@/game/hopper';
import { getAudioEngine } from '@/audio/engine';
import { useGameStore } from '@/store/game';
import ParallaxBackground from '@/components/ParallaxBackground.vue';
import GameGui from '@/components/GameGui.vue';

const canvasRef = ref<HTMLCanvasElement | null>(null);
const parallax = ref<InstanceType<typeof ParallaxBackground> | null>(null);
const game = useGameStore();
const isMuted = computed(() => game.muted);
const isPaused = computed(() => game.isPaused);

const currentButton = computed<'start' | 'restart' | null>(() => {
  if (game.phase === 'idle' || game.phase === 'ready') {
    return 'start';
  }

  if (game.phase === 'dying' || game.phase === 'gameover') {
    return 'restart';
  }

  return null;
});

let dispose: (() => void) | null = null;

function onToggleMute(): void {
  const audio = getAudioEngine();
  const next = !isMuted.value;

  try { audio.setMuted(next); } catch {}
  try { game.setMuted(next); } catch {}
}

function onTogglePause(): void {
  const next = !isPaused.value;

  try {
    if (next) {
      pauseGame();

      parallax.value?.pause();
    } else {
      resumeGame();

      if (game.phase !== 'idle' && game.phase !== 'ready') {
        parallax.value?.start();
      }
    }
  } catch {}
  try { if (next) game.pause(); else game.resume(); } catch {}
}

onMounted(() => {
  if (canvasRef.value) {
    dispose = initGame(
      canvasRef.value,
      () => {
        parallax.value?.stop();
      },
      () => {
        if (!isPaused.value) {
          parallax.value?.start();
        }
      }
    );
  }
  try { game.setMuted(getAudioEngine().isMuted()); } catch {}
  try { if (isGamePaused()) game.pause(); } catch {}
});

onUnmounted(() => {
  if (dispose) dispose();
  parallax.value?.stop();
});

function onStartClick(): void {
  try { startGame(); } catch {}
}

function onRestartClick(): void {
  try { restartGame(); } catch {}
}
</script>

<style lang="scss">
html, body {
  margin: 0;
  overflow: hidden;
  overscroll-behavior: none;
  background-color: rgb(3, 9, 11);
}

.game {
  position: relative;
  inset: 0;
  margin: auto;
  width: min(100vw, 1100px);
  height: min(100dvh, 800px);
  overflow: hidden;

  canvas#game {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    height: 100%;
    background: transparent;
    touch-action: none;
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
    contain: strict;
  }
}
</style>
